/**
 * Trusted-Origin Key Handover Panel — Owner-only diagnostic on /tenants.
 *
 * Surfaces BOTH ERIO ↔ ekselens.com shared secrets side-by-side:
 *
 *   1. `ERIO_TRUSTED_ORIGIN_KEY` (singular) — partner-assessment widget gate
 *   2. `ERIO_TRUSTED_ORIGIN_KEYS` (plural) — rate-limit lift on public endpoints
 *
 * Never reveals the full keys — only their fingerprints (last 6 chars).
 * The "Copy handover email" action drops a clean cut-and-paste cover note
 * into the clipboard, ready to paste into Slack/email next to the secrets
 * the Owner shares out-of-band via 1Password / Bitwarden / signed DM.
 *
 * Full canonical spec lives in `EKSELENS_INTEGRATION_CONTRACT.md`; the
 * practical runbook in `EKSELENS_INTEGRATION_HANDOVER.md`.
 */
import { useEffect, useState } from "react";
import { Key, Copy, CheckCircle2, AlertTriangle, ExternalLink } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const EMAIL_TEMPLATE = (partnerFp, rlKeys) => {
  const rlSummary = rlKeys && rlKeys.length
    ? rlKeys.map((k) => `${k.prefix ? `${k.prefix}:` : ""}…${k.fingerprint}`).join(", ")
    : "<not yet configured>";
  return `Hi,

ERIO is ready for the cross-domain integration with ekselens.com. Two
documents on the ERIO side:

  /app/memory/EKSELENS_INTEGRATION_CONTRACT.md   ← canonical spec (read first)
  /app/memory/EKSELENS_INTEGRATION_HANDOVER.md   ← practical runbook

Three concrete tasks on your side:

1. Store TWO shared secrets in your secret manager. Inject each one only
   server-side (never to the browser). I'll send the full values via
   1Password / Bitwarden / signed Slack DM — verify the fingerprints
   match what I have here:

     (a) ERIO_TRUSTED_ORIGIN_KEYS  — rate-limit lift on public endpoints.
         Header: X-ERIO-Trusted-Origin
         Fingerprint(s) configured: ${rlSummary}
         Endpoints: POST /api/leads, GET /api/public/search,
                    GET /api/public/supplier-lens/*, /api/newsletter/*
         Without it: ekselens.com server-side calls share the 10/min
                     anonymous bucket per IP. With it: 120/min per key.

     (b) ERIO_TRUSTED_ORIGIN_KEY  — partner-assessment widget secret.
         Header: X-Ekselens-Origin-Key
         Fingerprint configured: …${partnerFp || "<not yet configured>"}
         Endpoint: POST /api/partner/assessment
         Without it: the widget returns 401.

   See §2 of the handover doc for the side-by-side comparison table.

2. Paste the "Welcome back" continuity ribbon into the ekselens.com
   global layout. Drop-in snippet (no build step):

     /app/memory/snippets/welcome_back_ribbon.html

   It reads the ek_sso cookie ERIO sets on .ekselens.com when a user
   signs in to ERIO and surfaces a sticky "Welcome back — open ERIO →"
   bar plus a cross-domain "sign out" link.

3. Wire your "Sign in" and "Sign out" links to ERIO:

     Sign in   → https://erio.ekselens.com/login
     Sign out  → https://erio.ekselens.com/api/auth/sso-logout?return_to=https://www.ekselens.com/

When you're ready to flip prod, run through §6 "Smoke checklist" in the
handover doc — six curl commands + three UI steps, ~15 minutes end to end.

Shout if anything's unclear.
`;
};

function KeyCard({ title, envVar, headerName, purpose, configured, fingerprintLabel, lengthLabel, extra, testidPrefix }) {
  return (
    <div className="rounded-xl border border-black/5 bg-slate-50/50 p-4" data-testid={`${testidPrefix}-card`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-ek-ink">{title}</h3>
          <p className="mt-1 text-xs text-ek-ink/65">{purpose}</p>
        </div>
        <div className={`shrink-0 rounded-md px-2 py-0.5 text-[10px] font-semibold ${configured ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`} data-testid={`${testidPrefix}-status`}>
          {configured ? (
            <span className="inline-flex items-center gap-1"><CheckCircle2 size={11} /> Configured</span>
          ) : (
            <span className="inline-flex items-center gap-1"><AlertTriangle size={11} /> Not configured</span>
          )}
        </div>
      </div>
      <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
        <dt className="text-ek-ink/55">Env var</dt>
        <dd className="font-mono text-ek-ink">{envVar}</dd>
        <dt className="text-ek-ink/55">Header</dt>
        <dd className="font-mono text-ek-ink">{headerName}</dd>
        <dt className="text-ek-ink/55">Fingerprint</dt>
        <dd className="font-mono font-semibold text-ek-ink" data-testid={`${testidPrefix}-fingerprint`}>{fingerprintLabel}</dd>
        <dt className="text-ek-ink/55">Length</dt>
        <dd className="text-ek-ink">{lengthLabel}</dd>
        {extra}
      </dl>
    </div>
  );
}

export function TrustedOriginKeyHandoverPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    setLoading(true); setErr(null);
    try {
      const r = await api.get("/owner/integration/trusted-origin-key");
      setData(r.data);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Load failed");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const partner = data?.partner_widget_key;
  const rate = data?.rate_limit_keys;

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(EMAIL_TEMPLATE(
        partner?.fingerprint,
        rate?.keys || [],
      ));
      setCopied(true);
      toast.success("Handover email copied to clipboard");
      setTimeout(() => setCopied(false), 2500);
    } catch (e) {
      toast.error("Copy failed — your browser blocked clipboard access.");
    }
  };

  return (
    <section
      className="mt-6 rounded-2xl border border-black/5 bg-white p-6"
      data-testid="trusted-origin-key-panel"
    >
      <div className="flex items-start gap-3">
        <div className="rounded-lg bg-ek-purple/10 p-2 text-ek-purple">
          <Key size={18} />
        </div>
        <div className="flex-1">
          <h2 className="font-display text-lg font-semibold text-ek-ink">
            Ekselens trusted-origin keys · handover
          </h2>
          <p className="mt-0.5 text-sm text-ek-ink/65">
            Two distinct shared secrets gate the ERIO ↔ ekselens.com surface.
            Verify the fingerprints below match what you share with the
            ekselens.com engineer (out-of-band). Full spec:
            {" "}
            <span className="font-mono text-[11px]">EKSELENS_INTEGRATION_CONTRACT.md</span>.
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="rounded-md border border-black/10 px-2.5 py-1 text-xs font-semibold text-ek-ink hover:border-ek-purple/40 hover:text-ek-purple disabled:opacity-50"
          data-testid="trusted-origin-key-refresh"
        >{loading ? "…" : "Refresh"}</button>
      </div>

      {err && <p className="mt-3 text-xs text-rose-600" data-testid="trusted-origin-key-error">{err}</p>}

      {data && (
        <div className="mt-4 grid gap-3 lg:grid-cols-2">
          <KeyCard
            title="Partner-widget key"
            envVar={partner.env_var}
            headerName={partner.header_name}
            purpose={partner.purpose}
            configured={partner.configured}
            fingerprintLabel={partner.configured ? `…${partner.fingerprint}` : "—"}
            lengthLabel={partner.configured ? `${partner.length} chars` : "—"}
            testidPrefix="partner-key"
          />
          <KeyCard
            title="Rate-limit keys"
            envVar={rate.env_var}
            headerName={rate.header_name}
            purpose={rate.purpose}
            configured={rate.configured}
            fingerprintLabel={
              rate.configured
                ? rate.keys.map((k) => `${k.prefix ? `${k.prefix}:` : ""}…${k.fingerprint}`).join(", ")
                : "—"
            }
            lengthLabel={rate.configured ? `${rate.count} key${rate.count === 1 ? "" : "s"}` : "—"}
            testidPrefix="rate-key"
          />
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button
          onClick={copyEmail}
          disabled={!data}
          className="inline-flex items-center gap-1.5 rounded-md bg-ek-purple px-3 py-1.5 text-xs font-semibold text-white hover:bg-ek-purple/90 disabled:opacity-50"
          data-testid="trusted-origin-key-copy-email"
        >
          {copied ? <CheckCircle2 size={14} /> : <Copy size={14} />}
          {copied ? "Copied" : "Copy handover email"}
        </button>
        <a
          href="/app/memory/EKSELENS_INTEGRATION_HANDOVER.md"
          onClick={(e) => { e.preventDefault(); toast.message("Open EKSELENS_INTEGRATION_HANDOVER.md in your editor."); }}
          className="inline-flex items-center gap-1 text-xs font-semibold text-ek-purple hover:underline"
          data-testid="trusted-origin-key-doc-link"
        >
          Open the handover runbook <ExternalLink size={12} />
        </a>
        <a
          href="/app/memory/EKSELENS_INTEGRATION_CONTRACT.md"
          onClick={(e) => { e.preventDefault(); toast.message("Open EKSELENS_INTEGRATION_CONTRACT.md in your editor."); }}
          className="inline-flex items-center gap-1 text-xs font-semibold text-ek-ink/70 hover:text-ek-purple hover:underline"
          data-testid="trusted-origin-contract-link"
        >
          Canonical contract <ExternalLink size={12} />
        </a>
        {data && !rate?.configured && (
          <p className="text-xs text-amber-700">
            ⚠ ERIO_TRUSTED_ORIGIN_KEYS is empty — public endpoints have no rate-limit lift. Set in /app/backend/.env then restart.
          </p>
        )}
      </div>
    </section>
  );
}
